/**
 * MyAnalytics — the depositor's own analytics for their bound wallet, sourced
 * from the authenticated per-wallet analytics API (aggregated across the wallet's
 * mast). Split into mount-gated tabs so only the active section's hooks fetch:
 *
 *   Overview     — summary KPIs + per-vault breakdown + recent activity
 *   Performance  — cumulative PnL + portfolio value vs cost basis (charts)
 *   Deposits     — deposit timemap + history table
 *   Redeems      — redeem lifecycle (OPEN→SETTLED→CLAIMED) + claim timemap
 *   Cashflow     — daily inflow/outflow diverging bars + net capital
 *   Caps         — per-vault deposit headroom + consent status
 *
 * Wrapped by <WalletGate>, so it only renders once the bound wallet is connected.
 */

import { useState, type ReactNode } from "react";
import {
  useVaultConfig,
  useWalletSummary,
  useWalletPnlSeries,
  useWalletDeposits,
  useWalletRedeems,
  useWalletCashflows,
  useWalletActivity,
  useWalletCaps,
  isNotConfiguredError,
  type MastDepositorVaultLeg,
  type WalletActivityEvent,
  type WalletDepositEvent,
  type WalletRedeemRequest,
  type WalletCashflowPoint,
  type WalletPnlPoint,
  type WalletVaultCap,
} from "@thema/ui";
import { useAuth } from "../contexts/AuthContext";
import { CONSOLE_DEPLOYMENT_ID, CONSOLE_VAULT_REF } from "../console-config";
import { PageHead, StatTile, SectionTitle, Row, Callout, Badge, Skeleton } from "../components/primitives";
import { TimeSeriesChart, BarTimemap } from "../components/analytics-charts";
import { fmtUSD, fmtNum, shortenAddress } from "../lib/format";

const dash = "—";
const POS = "#0E8B62";
const NEG = "#C81644";
const ACCENT = "var(--accent)";

function pnlColor(v: number): string {
  return v > 0 ? POS : v < 0 ? NEG : "var(--text)";
}
function fmtCbtc(v: number): string {
  return v.toLocaleString("en-US", { maximumFractionDigits: 8 });
}
function tsShort(unix: number): string {
  return new Date(unix * 1000).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function dateShort(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

interface SectionProps {
  wallet: string;
  assetSymbol: string;
  assetDecimals: number;
}

// ─────────────────────────────── shared bits ────────────────────────────────

function ErrOrEmpty({ error, empty }: { error: Error | null; empty?: string }) {
  if (error && isNotConfiguredError(error)) {
    return <Callout tone="warn">Live data unavailable — the data API is not configured.</Callout>;
  }
  if (error && !/not\s*found|no depositor/i.test(error.message)) {
    return <Callout tone="error">Couldn't load: {error.message}</Callout>;
  }
  return <Callout tone="neutral">{empty ?? "No data yet."}</Callout>;
}

function Panel({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div className="card" style={{ padding: "18px 20px", display: "grid", gap: 14 }}>
      <SectionTitle right={right}>{title}</SectionTitle>
      {children}
    </div>
  );
}

// ───────────────────────────────── Overview ─────────────────────────────────

const ACTIVITY_LABEL: Record<string, string> = {
  DEPOSIT: "Deposit",
  REDEEM_REQUESTED: "Redeem requested",
  REDEEM_CLAIMED: "Redeem claimed",
};

function OverviewSection({ wallet, assetSymbol, assetDecimals }: SectionProps) {
  const summary = useWalletSummary(wallet, { assetDecimals });
  const activity = useWalletActivity(wallet, { assetDecimals, limit: 25 });
  const s = summary.data;
  const netPnl = s?.pnlUsd ?? 0;

  if (!summary.isLoading && !s) return <ErrOrEmpty error={summary.error} empty="No deposits found for your wallet yet." />;

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        <StatTile label="Current value" value={summary.isLoading ? <Skeleton w={110} /> : fmtUSD(s?.currentValueUsd ?? 0)} />
        <StatTile label="Total deposited" value={summary.isLoading ? <Skeleton w={110} /> : fmtUSD(s?.depositedUsd ?? 0)} sub={s ? `${fmtCbtc(s.depositedCbtc)} ${assetSymbol}` : undefined} />
        <StatTile label="Total redeemed" value={summary.isLoading ? <Skeleton w={110} /> : fmtUSD(s?.redeemedUsd ?? 0)} sub={s ? `${fmtCbtc(s.redeemedCbtc)} ${assetSymbol}` : undefined} />
        <StatTile label="Net PnL (USD)" value={summary.isLoading ? <Skeleton w={110} /> : fmtUSD(netPnl)} color={pnlColor(netPnl)} sub={s && s.depositedUsd > 0 ? `${((netPnl / s.depositedUsd) * 100).toFixed(1)}% on deposited` : undefined} />
      </div>

      <Panel title="Position">
        {summary.isLoading || !s ? (
          <Skeleton w="100%" h={80} />
        ) : (
          <div style={{ display: "grid", gap: 9 }}>
            <Row k="Shares held" v={fmtNum(s.perVault.reduce((acc: number, v: MastDepositorVaultLeg) => acc + v.sharesEstimated, 0), 6)} />
            <Row k="Pending redeem" v={s.pendingRedeemShares > 0 ? `${fmtNum(s.pendingRedeemShares, 6)} shares` : dash} />
            <Row k="First deposit" v={s.firstDepositAt ? tsShort(s.firstDepositAt) : dash} />
            <Row k="Last activity" v={s.lastActionAt ? tsShort(s.lastActionAt) : dash} />
            <Row k="Vaults in your mast" v={String(s.vaultIds.length)} sub={s.vaultIds.length > 1 ? "aggregated across a migration" : undefined} />
          </div>
        )}
      </Panel>

      {s && s.perVault.length > 1 && (
        <Panel title="Per-vault breakdown">
          <div style={{ display: "grid", gap: 14 }}>
            {s.perVault.map((leg: MastDepositorVaultLeg) => (
              <div key={leg.vaultId} style={{ display: "grid", gap: 7 }}>
                <div className="mono" style={{ fontSize: 11.5, color: "var(--text-soft)" }}>{leg.vaultId}</div>
                <Row k="Deposited" v={fmtUSD(leg.depositedUsd)} />
                <Row k="Current value" v={fmtUSD(leg.currentValueUsd)} />
                <Row k="PnL" v={<span style={{ color: pnlColor(leg.pnlUsd) }}>{fmtUSD(leg.pnlUsd)}</span>} strong />
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Recent activity" right={<span className="eyebrow">Deposits · redeems · claims</span>}>
        {activity.isLoading ? (
          <Skeleton w="100%" h={120} />
        ) : (activity.data?.data.length ?? 0) === 0 ? (
          <div style={{ fontSize: 13, color: "var(--micro)" }}>No activity yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {activity.data?.data.map((e: WalletActivityEvent, i: number) => (
              <div key={`${e.txHash}-${e.type}-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Badge variant={e.type === "DEPOSIT" ? "pos" : e.type === "REDEEM_CLAIMED" ? "live" : "neutral"}>{ACTIVITY_LABEL[e.type] ?? e.type}</Badge>
                  <span style={{ fontSize: 12, color: "var(--micro)" }}>{tsShort(e.timestamp)}</span>
                </div>
                <span className="mono tnum" style={{ fontSize: 13, fontWeight: 600 }}>{fmtUSD(e.amountUsd)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ─────────────────────────────── Performance ────────────────────────────────

function PerformanceSection({ wallet, assetDecimals }: SectionProps) {
  const series = useWalletPnlSeries(wallet, { assetDecimals });
  const pts = series.data?.data ?? [];
  const last = pts[pts.length - 1];

  if (series.isLoading) return <Skeleton w="100%" h={260} />;
  if (!series.isSuccess || pts.length === 0) return <ErrOrEmpty error={series.error} empty="No performance history yet." />;

  const peak = pts.reduce((m: number, p: WalletPnlPoint) => Math.max(m, p.pnlUsd), -Infinity);
  const trough = pts.reduce((m: number, p: WalletPnlPoint) => Math.min(m, p.pnlUsd), Infinity);

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        <StatTile label="PnL now" value={fmtUSD(last?.pnlUsd ?? 0)} color={pnlColor(last?.pnlUsd ?? 0)} />
        <StatTile label="Peak PnL" value={fmtUSD(peak)} color={pnlColor(peak)} />
        <StatTile label="Lowest PnL" value={fmtUSD(trough)} color={pnlColor(trough)} />
      </div>

      <Panel title="Cumulative PnL (USD)">
        <TimeSeriesChart
          points={pts}
          height={240}
          valueFmt={(v) => fmtUSD(v)}
          lines={[{ label: "PnL", color: (last?.pnlUsd ?? 0) >= 0 ? POS : NEG, get: (p) => p.pnlUsd, area: true }]}
        />
      </Panel>

      <Panel title="Portfolio value vs cost basis">
        <TimeSeriesChart
          points={pts}
          height={240}
          valueFmt={(v) => fmtUSD(v)}
          lines={[
            { label: "Value", color: ACCENT, get: (p) => p.valueUsd, area: true },
            { label: "Cost basis", color: "var(--micro)", get: (p) => p.costBasisUsd, dashed: true },
          ]}
        />
        <div style={{ fontSize: 11.5, color: "var(--micro)" }}>
          The gap between value and cost basis is your unrealized PnL.
        </div>
      </Panel>
    </div>
  );
}

// ──────────────────────────────── Deposits ──────────────────────────────────

function DepositsSection({ wallet, assetSymbol, assetDecimals }: SectionProps) {
  const cashflows = useWalletCashflows(wallet, { assetDecimals });
  const deposits = useWalletDeposits(wallet, { assetDecimals, limit: 100 });
  const flows = cashflows.data?.data ?? [];
  const rows = deposits.data?.data ?? [];

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <Panel title="Deposit timemap (USD / day)">
        {cashflows.isLoading ? (
          <Skeleton w="100%" h={200} />
        ) : (
          <BarTimemap bars={flows} up={{ color: POS, get: (b) => b.depositUsd, label: "Deposited" }} valueFmt={(v) => fmtUSD(v)} />
        )}
      </Panel>

      <Panel title="Deposit history" right={<span className="eyebrow">{rows.length} deposits</span>}>
        {deposits.isLoading ? (
          <Skeleton w="100%" h={120} />
        ) : rows.length === 0 ? (
          <ErrOrEmpty error={deposits.error} empty="No deposits yet." />
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {rows.map((d: WalletDepositEvent, i: number) => (
              <div key={`${d.txHash}-${i}`} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 12, alignItems: "baseline" }}>
                <span style={{ fontSize: 12.5, color: "var(--micro)" }}>{tsShort(d.timestamp)}</span>
                <span className="mono tnum" style={{ fontSize: 12.5, color: "var(--text-soft)" }}>{fmtCbtc(d.amountCbtc)} {assetSymbol}</span>
                <span className="mono tnum" style={{ fontSize: 13, fontWeight: 600, minWidth: 90, textAlign: "right" }}>{fmtUSD(d.amountUsd)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ──────────────────────────────── Redeems ───────────────────────────────────

function redeemBadge(status: string): "armed" | "accent" | "live" | "neutral" {
  if (status === "OPEN") return "armed";
  if (status === "SETTLED") return "accent";
  if (status === "CLAIMED") return "live";
  return "neutral";
}

function RedeemsSection({ wallet, assetSymbol, assetDecimals }: SectionProps) {
  const cashflows = useWalletCashflows(wallet, { assetDecimals });
  const redeems = useWalletRedeems(wallet, { assetDecimals, limit: 100 });
  const flows = cashflows.data?.data ?? [];
  const rows = redeems.data?.data ?? [];

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <Panel title="Claimed redemptions timemap (USD / day)">
        {cashflows.isLoading ? (
          <Skeleton w="100%" h={200} />
        ) : (
          <BarTimemap bars={flows} up={{ color: NEG, get: (b) => b.redeemUsd, label: "Claimed" }} valueFmt={(v) => fmtUSD(v)} />
        )}
      </Panel>

      <Panel title="Redeem requests" right={<span className="eyebrow">{rows.length} requests</span>}>
        {redeems.isLoading ? (
          <Skeleton w="100%" h={120} />
        ) : rows.length === 0 ? (
          <ErrOrEmpty error={redeems.error} empty="No redemptions yet." />
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {rows.map((r: WalletRedeemRequest) => (
              <div key={`${r.vaultId}-${r.requestId}`} style={{ display: "grid", gap: 6, borderBottom: "1px solid var(--border)", paddingBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Badge variant={redeemBadge(r.status)}>{r.status}</Badge>
                    <span style={{ fontSize: 12.5, color: "var(--micro)" }}>#{r.requestId}</span>
                  </div>
                  <span className="mono tnum" style={{ fontSize: 13, fontWeight: 600 }}>{fmtNum(r.shares, 6)} shares</span>
                </div>
                <Row k="Requested" v={tsShort(r.requestedAt)} />
                {r.status !== "CLAIMED" ? (
                  <Row k="Est. payout" v={r.estimatedPayoutUsd > 0 ? fmtUSD(r.estimatedPayoutUsd) : dash} />
                ) : (
                  <>
                    <Row k="Claimed" v={r.claimedAt ? tsShort(r.claimedAt) : dash} />
                    <Row k="Received" v={r.claimedUsd != null ? `${fmtUSD(r.claimedUsd)} · ${fmtCbtc(r.claimedCbtc ?? 0)} ${assetSymbol}` : dash} strong />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ──────────────────────────────── Cashflow ──────────────────────────────────

function CashflowSection({ wallet, assetDecimals }: SectionProps) {
  const cashflows = useWalletCashflows(wallet, { assetDecimals });
  const flows = cashflows.data?.data ?? [];
  const totalIn = flows.reduce((a: number, f: WalletCashflowPoint) => a + f.depositUsd, 0);
  const totalOut = flows.reduce((a: number, f: WalletCashflowPoint) => a + f.redeemUsd, 0);
  const net = totalIn - totalOut;

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        <StatTile label="Total in" value={fmtUSD(totalIn)} color={POS} />
        <StatTile label="Total out" value={fmtUSD(totalOut)} color={NEG} />
        <StatTile label="Net deployed" value={fmtUSD(net)} color={pnlColor(net)} />
      </div>
      <Panel title="Daily cashflow — deposits vs claimed redemptions">
        {cashflows.isLoading ? (
          <Skeleton w="100%" h={220} />
        ) : (
          <BarTimemap
            bars={flows}
            height={220}
            up={{ color: POS, get: (b) => b.depositUsd, label: "Deposited" }}
            down={{ color: NEG, get: (b) => b.redeemUsd, label: "Claimed out" }}
            valueFmt={(v) => fmtUSD(v)}
          />
        )}
      </Panel>
    </div>
  );
}

// ────────────────────────────────── Caps ────────────────────────────────────

function CapsSection({ wallet, assetDecimals }: SectionProps) {
  const caps = useWalletCaps(wallet, { assetDecimals });
  const rows = caps.data?.data ?? [];

  if (caps.isLoading) return <Skeleton w="100%" h={160} />;
  if (rows.length === 0) return <ErrOrEmpty error={caps.error} empty="No cap data yet." />;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      {rows.map((c: WalletVaultCap) => {
        const used = c.effectiveCapUsd > 0 ? Math.min(1, c.cumDepositedUsd / c.effectiveCapUsd) : 0;
        return (
          <Panel key={c.vaultId} title={c.vaultId} right={c.hasOverride ? <Badge variant="accent">custom cap</Badge> : <span className="eyebrow">default cap</span>}>
            <div style={{ display: "grid", gap: 10 }}>
              <Row k="Deposit cap" v={fmtUSD(c.effectiveCapUsd)} />
              <Row k="Deposited" v={fmtUSD(c.cumDepositedUsd)} />
              <Row k="Headroom" v={fmtUSD(c.headroomUsd)} strong />
              <div style={{ height: 8, background: "var(--surface-alt)", borderRadius: 6, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${used * 100}%`, background: used > 0.9 ? NEG : ACCENT, borderRadius: 6 }} />
              </div>
              <Row k="Consent" v={c.consentAt ? `Signed ${dateShort(c.consentAt)}` : c.consentHash ? "On record" : dash} />
            </div>
          </Panel>
        );
      })}
    </div>
  );
}

// ──────────────────────────────── container ─────────────────────────────────

type TabKey = "overview" | "performance" | "deposits" | "redeems" | "cashflow" | "caps";
const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "performance", label: "Performance" },
  { key: "deposits", label: "Deposits" },
  { key: "redeems", label: "Redeems" },
  { key: "cashflow", label: "Cashflow" },
  { key: "caps", label: "Caps" },
];

export default function MyAnalytics() {
  const { boundWallet } = useAuth();
  const vault = useVaultConfig(CONSOLE_DEPLOYMENT_ID, CONSOLE_VAULT_REF);
  const [tab, setTab] = useState<TabKey>("overview");

  const assetSymbol = vault?.assetSymbol || "cBTC";
  const assetDecimals = vault?.assetDecimals ?? 8;
  const wallet = boundWallet ?? "";
  const props: SectionProps = { wallet, assetSymbol, assetDecimals };

  return (
    <div style={{ display: "grid", gap: 24 }}>
      <PageHead
        eyebrow="Your position"
        title="My analytics"
        sub={boundWallet ? `Aggregated across your full deposit history · ${shortenAddress(boundWallet)}` : undefined}
      />

      {/* segmented tab control */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid var(--border)", paddingBottom: 2 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="mono"
            style={{
              appearance: "none",
              border: "none",
              background: "none",
              cursor: "pointer",
              padding: "8px 12px",
              fontSize: 12,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontWeight: 600,
              color: tab === t.key ? "var(--text)" : "var(--micro)",
              borderBottom: tab === t.key ? "2px solid var(--accent)" : "2px solid transparent",
              marginBottom: -3,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Only the active tab mounts → only its hooks fetch. */}
      {tab === "overview" && <OverviewSection {...props} />}
      {tab === "performance" && <PerformanceSection {...props} />}
      {tab === "deposits" && <DepositsSection {...props} />}
      {tab === "redeems" && <RedeemsSection {...props} />}
      {tab === "cashflow" && <CashflowSection {...props} />}
      {tab === "caps" && <CapsSection {...props} />}
    </div>
  );
}
